import React, { useState, useEffect, useCallback } from 'react';
import { Settings, BarChart3, Moon, Sun, Maximize, Minimize, RotateCcw, Copy, Download, Upload, Shuffle, Shield, Info, Binary, Key } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface CipherStats {
  totalEncryptions: number;
  totalDecryptions: number;
  totalOperations: number;
  totalCharactersProcessed: number;
  averageTextLength: number;
  longestText: number;
  binaryGroupsProcessed: number;
  averageBinaryGroups: number;
  totalSteganographicTexts: number;
  uniqueCoverTexts: Set<string>;
  averageCoverTextLength: number;
}

interface CipherHistory {
  id: string;
  input: string;
  output: string;
  coverText: string;
  operation: 'encrypt' | 'decrypt';
  timestamp: Date;
  textLength: number;
  binaryGroups: number;
  method: 'standard' | 'variant';
}

const BaconCipherApp: React.FC = () => {
  // Core state
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [coverText, setCoverText] = useState<string>('This is a sample cover text that will hide the secret message using the Bacon cipher method.');
  const [operation, setOperation] = useState<'encrypt' | 'decrypt'>('encrypt');
  
  // UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<'settings' | 'analytics' | 'help' | 'binary' | null>(null);
  
  // Advanced options state
  const [preserveCase, setPreserveCase] = useState<boolean>(true);
  const [preserveSpaces, setPreserveSpaces] = useState<boolean>(false);
  const [preservePunctuation, setPreservePunctuation] = useState<boolean>(false);
  const [showBinary, setShowBinary] = useState<boolean>(false);
  const [groupOutput, setGroupOutput] = useState<boolean>(false);
  const [groupSize, setGroupSize] = useState<number>(5);
  const [baconMethod, setBaconMethod] = useState<'standard' | 'variant'>('standard');
  const [steganographicMethod, setSteganographicMethod] = useState<'case' | 'font' | 'spacing'>('case');
  
  // Analytics state
  const [stats, setStats] = useState<CipherStats>({
    totalEncryptions: 0,
    totalDecryptions: 0,
    totalOperations: 0,
    totalCharactersProcessed: 0,
    averageTextLength: 0,
    longestText: 0,
    binaryGroupsProcessed: 0,
    averageBinaryGroups: 0,
    totalSteganographicTexts: 0,
    uniqueCoverTexts: new Set<string>(),
    averageCoverTextLength: 0
  });
  const [history, setHistory] = useState<CipherHistory[]>([]);
  const [binaryRepresentation, setBinaryRepresentation] = useState<string>('');

  // Bacon cipher alphabet mappings
  const baconStandard: { [key: string]: string } = {
    'A': 'AAAAA', 'B': 'AAAAB', 'C': 'AAABA', 'D': 'AAABB', 'E': 'AABAA',
    'F': 'AABAB', 'G': 'AABBA', 'H': 'AABBB', 'I': 'ABAAA', 'J': 'ABAAA',
    'K': 'ABAAB', 'L': 'ABABA', 'M': 'ABABB', 'N': 'ABBAA', 'O': 'ABBAB',
    'P': 'ABBBA', 'Q': 'ABBBB', 'R': 'BAAAA', 'S': 'BAAAB', 'T': 'BAABA',
    'U': 'BAABB', 'V': 'BAABB', 'W': 'BABAA', 'X': 'BABAB', 'Y': 'BABBA',
    'Z': 'BABBB'
  };

  const baconVariant: { [key: string]: string } = {
    'A': 'AAAAA', 'B': 'AAAAB', 'C': 'AAABA', 'D': 'AAABB', 'E': 'AABAA',
    'F': 'AABAB', 'G': 'AABBA', 'H': 'AABBB', 'I': 'ABAAA', 'J': 'ABAAB',
    'K': 'ABABA', 'L': 'ABABB', 'M': 'ABBAA', 'N': 'ABBAB', 'O': 'ABBBA',
    'P': 'ABBBB', 'Q': 'BAAAA', 'R': 'BAAAB', 'S': 'BAABA', 'T': 'BAABB',
    'U': 'BABAA', 'V': 'BABAB', 'W': 'BABBA', 'X': 'BABBB', 'Y': 'BBAAA',
    'Z': 'BBAAB'
  };

  // Get current Bacon alphabet
  const getBaconAlphabet = useCallback(() => {
    return baconMethod === 'standard' ? baconStandard : baconVariant;
  }, [baconMethod]);

  // Get reverse Bacon alphabet
  const getReverseBaconAlphabet = useCallback(() => {
    const alphabet = getBaconAlphabet();
    const reverse: { [key: string]: string } = {};
    Object.entries(alphabet).forEach(([letter, code]) => {
      reverse[code] = letter;
    });
    return reverse;
  }, [getBaconAlphabet]);

  // Clean text based on preservation settings
  const cleanText = useCallback((text: string): string => {
    let cleaned = text;
    
    if (!preserveSpaces) {
      cleaned = cleaned.replace(/\s/g, '');
    }
    
    if (!preservePunctuation) {
      cleaned = cleaned.replace(/[^\w\s]/g, '');
    }
    
    if (!preserveCase) {
      cleaned = cleaned.toUpperCase();
    }
    
    return cleaned;
  }, [preserveSpaces, preservePunctuation, preserveCase]);

  // Convert text to Bacon binary
  const textToBacon = useCallback((text: string): string => {
    const alphabet = getBaconAlphabet();
    const cleanedText = text.replace(/[^A-Za-z]/g, '').toUpperCase();
    let result = '';
    
    for (const char of cleanedText) {
      if (alphabet[char]) {
        result += alphabet[char];
      }
    }
    
    return result;
  }, [getBaconAlphabet]);

  // Convert Bacon binary to text
  const baconToText = useCallback((binary: string): string => {
    const reverseAlphabet = getReverseBaconAlphabet();
    let result = '';
    
    // Process binary in groups of 5
    for (let i = 0; i < binary.length; i += 5) {
      const group = binary.substr(i, 5);
      if (group.length === 5 && reverseAlphabet[group]) {
        result += reverseAlphabet[group];
      }
    }
    
    return result;
  }, [getReverseBaconAlphabet]);

  // Apply steganographic method to cover text
  const applySteganography = useCallback((coverText: string, binary: string): string => {
    const letters = coverText.replace(/[^A-Za-z]/g, '');
    if (letters.length < binary.length) {
      return 'Error: Cover text too short for message';
    }
    
    let result = coverText;
    let letterIndex = 0;
    let binaryIndex = 0;
    
    if (steganographicMethod === 'case') {
      // Use case to represent A/B (lowercase = A, uppercase = B)
      for (let i = 0; i < result.length && binaryIndex < binary.length; i++) {
        if (/[A-Za-z]/.test(result[i])) {
          const bit = binary[binaryIndex];
          if (bit === 'A') {
            result = result.substring(0, i) + result[i].toLowerCase() + result.substring(i + 1);
          } else if (bit === 'B') {
            result = result.substring(0, i) + result[i].toUpperCase() + result.substring(i + 1);
          }
          binaryIndex++;
        }
      }
    } else if (steganographicMethod === 'font') {
      // Use italic markers to represent A/B (normal = A, *italic* = B)
      let modifiedResult = '';
      let i = 0;
      binaryIndex = 0;
      
      while (i < result.length && binaryIndex < binary.length) {
        if (/[A-Za-z]/.test(result[i])) {
          const bit = binary[binaryIndex];
          if (bit === 'A') {
            modifiedResult += result[i];
          } else if (bit === 'B') {
            modifiedResult += '*' + result[i] + '*';
          }
          binaryIndex++;
        } else {
          modifiedResult += result[i];
        }
        i++;
      }
      // Add remaining characters
      modifiedResult += result.substring(i);
      result = modifiedResult;
      
    } else if (steganographicMethod === 'spacing') {
      // Use spacing to represent A/B (single space = A, double space = B)
      result = result.replace(/\s+/g, ' '); // Normalize spaces first
      let modifiedResult = '';
      let i = 0;
      binaryIndex = 0;
      
      while (i < result.length) {
        if (result[i] === ' ' && binaryIndex < binary.length) {
          const bit = binary[binaryIndex];
          if (bit === 'A') {
            modifiedResult += ' ';
          } else if (bit === 'B') {
            modifiedResult += '  ';
          }
          binaryIndex++;
        } else {
          modifiedResult += result[i];
        }
        i++;
      }
      result = modifiedResult;
    }
    
    return result;
  }, [steganographicMethod]);

  // Extract steganographic information from text
  const extractSteganography = useCallback((text: string): string => {
    let binary = '';
    
    if (steganographicMethod === 'case') {
      // Extract case pattern
      for (const char of text) {
        if (/[a-z]/.test(char)) {
          binary += 'A';
        } else if (/[A-Z]/.test(char)) {
          binary += 'B';
        }
      }
    } else if (steganographicMethod === 'font') {
      // Extract font markers
      let i = 0;
      while (i < text.length) {
        if (text[i] === '*' && i + 2 < text.length && text[i + 2] === '*') {
          // Found italic marker
          binary += 'B';
          i += 3; // Skip past the marked character
        } else if (/[A-Za-z]/.test(text[i])) {
          binary += 'A';
          i++;
        } else {
          i++;
        }
      }
    } else if (steganographicMethod === 'spacing') {
      // Extract spacing pattern
      const spaces = text.match(/\s+/g) || [];
      for (const space of spaces) {
        if (space.length === 1) {
          binary += 'A';
        } else if (space.length >= 2) {
          binary += 'B';
        }
      }
    }
    
    return binary;
  }, [steganographicMethod]);

  // Bacon cipher encryption
  const baconEncrypt = useCallback((text: string, cover: string): string => {
    if (!text || !cover) return '';
    
    const binary = textToBacon(text);
    setBinaryRepresentation(binary);
    
    if (binary.length === 0) return '';
    
    const result = applySteganography(cover, binary);
    
    if (groupOutput && result && !result.startsWith('Error:')) {
      const groups = [];
      for (let i = 0; i < result.length; i += groupSize) {
        groups.push(result.slice(i, i + groupSize));
      }
      return groups.join(' ');
    }
    
    return result;
  }, [textToBacon, applySteganography, groupOutput, groupSize]);

  // Bacon cipher decryption
  const baconDecrypt = useCallback((text: string): string => {
    if (!text) return '';
    
    // Remove grouping spaces if present
    const cleanedText = groupOutput ? text.replace(/\s+/g, ' ') : text;
    
    const binary = extractSteganography(cleanedText);
    setBinaryRepresentation(binary);
    
    if (binary.length === 0) return '';
    
    const result = baconToText(binary);
    return result;
  }, [extractSteganography, baconToText, groupOutput]);

  // Process text - main cipher logic
  const processText = useCallback(() => {
    if (operation === 'encrypt' && (!inputText || !coverText)) {
      setOutputText('');
      return '';
    } else if (operation === 'decrypt' && !inputText) {
      setOutputText('');
      return '';
    }
    
    const result = operation === 'encrypt' 
      ? baconEncrypt(inputText, coverText)
      : baconDecrypt(inputText);
    
    setOutputText(result);
    return result;
  }, [inputText, coverText, operation, baconEncrypt, baconDecrypt]);

  // Update analytics
  const updateAnalytics = useCallback((result: string) => {
    if (!inputText || !result || result.startsWith('Error:')) return;
    
    const cleanInput = cleanText(inputText);
    const binaryGroups = Math.ceil(cleanInput.replace(/[^A-Za-z]/g, '').length);
    
    const newHistoryItem: CipherHistory = {
      id: Date.now().toString(),
      input: inputText,
      output: result,
      coverText: coverText,
      operation,
      timestamp: new Date(),
      textLength: inputText.length,
      binaryGroups: binaryGroups,
      method: baconMethod
    };
    
    setHistory(prev => [newHistoryItem, ...prev.slice(0, 49)]);
    
    setStats(prevStats => {
      const newUniqueCoverTexts = new Set(Array.from(prevStats.uniqueCoverTexts).concat([coverText.substring(0, 50)]));
      const totalOps = prevStats.totalOperations + 1;
      const totalChars = prevStats.totalCharactersProcessed + inputText.length;
      
      return {
        ...prevStats,
        totalEncryptions: prevStats.totalEncryptions + (operation === 'encrypt' ? 1 : 0),
        totalDecryptions: prevStats.totalDecryptions + (operation === 'decrypt' ? 1 : 0),
        totalOperations: totalOps,
        totalCharactersProcessed: totalChars,
        averageTextLength: Math.round(totalChars / totalOps),
        longestText: Math.max(prevStats.longestText, inputText.length),
        binaryGroupsProcessed: prevStats.binaryGroupsProcessed + binaryGroups,
        averageBinaryGroups: Math.round((prevStats.binaryGroupsProcessed + binaryGroups) / totalOps),
        totalSteganographicTexts: prevStats.totalSteganographicTexts + 1,
        uniqueCoverTexts: newUniqueCoverTexts,
        averageCoverTextLength: Math.round(Array.from(newUniqueCoverTexts).reduce((sum, t) => sum + t.length, 0) / newUniqueCoverTexts.size)
      };
    });
  }, [inputText, coverText, operation, cleanText, baconMethod]);

  // Auto-process when inputs change
  useEffect(() => {
    if (inputText && (operation === 'decrypt' || coverText)) {
      const result = processText();
      if (result) {
        updateAnalytics(result);
      }
    } else {
      setOutputText('');
      setBinaryRepresentation('');
    }
  }, [inputText, coverText, operation, baconMethod, steganographicMethod]);

  // Utility functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputText(content);
      };
      reader.readAsText(file);
    }
  };

  const resetAll = () => {
    setInputText('');
    setOutputText('');
    setCoverText('This is a sample cover text that will hide the secret message using the Bacon cipher method.');
    setOperation('encrypt');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const swapTexts = () => {
    const temp = inputText;
    setInputText(outputText.replace(/\s/g, ''));
    setOutputText(temp);
    setOperation(operation === 'encrypt' ? 'decrypt' : 'encrypt');
  };

  const generateSampleText = () => {
    const samples = [
      "MEET ME AT MIDNIGHT",
      "THE ENEMY IS NEAR",
      "ATTACK AT DAWN",
      "SECRET MISSION COMPLETE",
      "BACON CIPHER IS SECURE",
      "HIDDEN IN PLAIN SIGHT",
      "STEGANOGRAPHY WORKS"
    ];
    const randomSample = samples[Math.floor(Math.random() * samples.length)];
    setInputText(randomSample);
  };

  const generateSampleCoverText = () => {
    const coverTexts = [
      "The quick brown fox jumps over the lazy dog. This sentence contains every letter of the alphabet and makes for an excellent cover text.",
      "In a hole in the ground there lived a hobbit. Not a nasty, dirty, wet hole filled with the ends of worms and an oozy smell.",
      "It was the best of times, it was the worst of times, it was the age of wisdom, it was the age of foolishness.",
      "To be or not to be, that is the question. Whether tis nobler in the mind to suffer the slings and arrows of outrageous fortune.",
      "Four score and seven years ago our fathers brought forth on this continent a new nation conceived in liberty and dedicated.",
      "Call me Ishmael. Some years ago never mind how long precisely having little or no money in my purse and nothing particular."
    ];
    const randomCoverText = coverTexts[Math.floor(Math.random() * coverTexts.length)];
    setCoverText(randomCoverText);
  };

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>Bacon Cipher - Advanced Binary Steganographic Encryption Tool | Free Online Cryptography</title>
        <meta name="description" content="Professional Bacon Cipher tool with binary encoding, steganographic hiding methods, and historical Francis Bacon cipher features. Perfect for cryptography education and steganography research." />
        <meta name="keywords" content="bacon cipher, steganographic cipher, binary cipher, francis bacon cipher, steganography, hidden message, binary encoding, classical cryptography, invisible cipher, secret writing, cover text, steganographic encryption, cryptanalysis, security education" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Bacon Cipher - Advanced Steganographic Encryption Tool" />
        <meta property="og:description" content="Professional Bacon Cipher with binary encoding, steganographic methods, and historical Francis Bacon cipher features." />
        <meta property="og:image" content="/api/placeholder/1200/630" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta property="og:site_name" content="Bacon Cipher Tool" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Bacon Cipher - Historical Steganographic Encryption" />
        <meta name="twitter:description" content="Professional Bacon Cipher with binary encoding and educational features. Free classical cryptography tool." />
        <meta name="twitter:image" content="/api/placeholder/1200/630" />
        <meta name="twitter:creator" content="@baconcipher" />
        
        {/* Additional Meta Tags */}
        <meta name="author" content="Bacon Cipher Tool" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        
        {/* Historical & Scientific Keywords */}
        <meta name="subject" content="Renaissance History, Cryptography, Classical Ciphers, Francis Bacon" />
        <meta name="classification" content="Education, History, Science, Computer Science" />
        
        {/* Mobile Optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content={isDarkMode ? 'black-translucent' : 'default'} />
        <meta name="apple-mobile-web-app-title" content="Bacon Cipher" />
        
        {/* Theme Color */}
        <meta name="theme-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        <meta name="msapplication-navbutton-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : ''} />
        
        {/* Structured Data - JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Bacon Cipher Tool",
            "alternateName": ["Francis Bacon Cipher", "Binary Steganographic Cipher", "Steganography Tool"],
            "description": "Advanced Bacon Cipher tool for binary steganographic encryption with cover text hiding, multiple encoding methods, and historical Francis Bacon cipher features.",
            "url": typeof window !== 'undefined' ? window.location.href : '',
            "applicationCategory": ["SecurityApplication", "EducationalApplication", "HistoricalApplication"],
            "operatingSystem": "Any",
            "browserRequirements": "HTML5, JavaScript",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Real-time Bacon Cipher encryption and decryption",
              "Binary pattern visualization",
              "Multiple steganographic hiding methods",
              "Historical Francis Bacon cipher implementation",
              "Cover text generation and analysis",
              "Advanced cipher options and settings",
              "Analytics and usage statistics",
              "Educational cipher information",
              "Dark and light themes",
              "File upload and download",
              "Mobile responsive design"
            ],
            "screenshot": "/api/placeholder/1200/630",
            "softwareVersion": "1.0.0",
            "datePublished": "2025-01-01",
            "dateModified": new Date().toISOString().split('T')[0],
            "author": {
              "@type": "Organization",
              "name": "Bacon Cipher Tool",
              "description": "Historical cryptography and steganographic cipher education tools"
            },
            "educationalAlignment": {
              "@type": "AlignmentObject",
              "alignmentType": "teaches",
              "educationalFramework": "Renaissance History and Cryptography Education",
              "targetName": "Classical Cryptography and Historical Steganographic Methods"
            },
            "learningResourceType": "Interactive Tool",
            "educationalUse": ["instruction", "research", "historical study"],
            "typicalAgeRange": "16-99",
            "inLanguage": "en",
            "isAccessibleForFree": true,
            "about": [
              {
                "@type": "Thing",
                "name": "Renaissance Cryptography"
              },
              {
                "@type": "Thing",
                "name": "Francis Bacon"
              },
              {
                "@type": "Thing",
                "name": "Steganographic Cipher Systems"
              }
            ],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "ratingCount": "743",
              "bestRating": "5",
              "worstRating": "1"
            }
          })}
        </script>
        
        {/* Historical Renaissance Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HistoricalDigitalObject",
            "name": "Bacon Cipher Historical Implementation",
            "description": "Digital recreation of the Bacon cipher system attributed to Francis Bacon, representing one of the earliest forms of binary encoding and steganographic communication.",
            "dateCreated": "1605",
            "creator": {
              "@type": "Person",
              "name": "Francis Bacon"
            },
            "about": {
              "@type": "HistoricalEvent",
              "name": "Renaissance Scientific Revolution"
            },
            "culturalContext": "English Renaissance Scientific and Literary Period",
            "historicalSignificance": "First systematic binary encoding system and early steganographic cipher method"
          })}
        </script>
        
        {/* Educational Renaissance History Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LearningResource",
            "name": "Bacon Cipher Renaissance History Tutorial",
            "description": "Learn about Renaissance cryptography through hands-on interaction with the historic Bacon cipher system attributed to Francis Bacon.",
            "educationalLevel": "intermediate",
            "teaches": [
              "Binary encoding systems",
              "Renaissance cryptography history", 
              "Steganographic methods",
              "Francis Bacon scientific contributions",
              "Classical cipher analysis"
            ],
            "learningResourceType": "Interactive Tool",
            "interactivityType": "active",
            "educationalUse": ["instruction", "research", "historical analysis"],
            "audience": {
              "@type": "EducationalAudience",
              "educationalRole": ["student", "teacher", "historian", "researcher"],
              "audienceType": ["renaissance history student", "cryptography enthusiast"]
            },
            "about": [
              {
                "@type": "Thing",
                "name": "Renaissance History"
              },
              {
                "@type": "Thing",
                "name": "Francis Bacon"
              },
              {
                "@type": "Thing",
                "name": "Steganographic Cryptography"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      } ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        
        {/* Header */}
        <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Binary className="text-purple-500" size={24} />
              <div>
                <h1 className="text-2xl font-bold">Bacon Cipher</h1>
                <p className="text-sm opacity-60">Binary Steganographic Cipher (1605)</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Binary Viewer */}
              <button
                onClick={() => setActiveModal(activeModal === 'binary' ? null : 'binary')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="View Binary Pattern"
              >
                <Binary size={20} />
              </button>
              
              {/* Help */}
              <button
                onClick={() => setActiveModal(activeModal === 'help' ? null : 'help')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Help & Information"
              >
                <Info size={20} />
              </button>
              
              {/* Advanced Options */}
              <button
                onClick={() => setActiveModal(activeModal === 'settings' ? null : 'settings')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Advanced Options"
              >
                <Settings size={20} />
              </button>
              
              {/* Analytics */}
              <button
                onClick={() => setActiveModal(activeModal === 'analytics' ? null : 'analytics')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Analytics"
              >
                <BarChart3 size={20} />
              </button>
              
              {/* Theme Toggle */}
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Theme"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              
              {/* Fullscreen Toggle */}
              <button
                onClick={toggleFullscreen}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Fullscreen"
              >
                {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            
            {/* Controls */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Operation</label>
                  <select
                    value={operation}
                    onChange={(e) => setOperation(e.target.value as 'encrypt' | 'decrypt')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="encrypt">🔒 Hide Message</option>
                    <option value="decrypt">🔍 Extract Message</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Bacon Method</label>
                  <select
                    value={baconMethod}
                    onChange={(e) => setBaconMethod(e.target.value as 'standard' | 'variant')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="standard">Standard (I=J, U=V)</option>
                    <option value="variant">Variant (All 26)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Hide Method</label>
                  <select
                    value={steganographicMethod}
                    onChange={(e) => setSteganographicMethod(e.target.value as 'case' | 'font' | 'spacing')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="case">Case (a=A, A=B)</option>
                    <option value="font">Font (*a*=B)</option>
                    <option value="spacing">Spacing (  =B)</option>
                  </select>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={swapTexts}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-purple-600 border-purple-500 hover:bg-purple-700 text-white' : 'bg-purple-500 border-purple-400 hover:bg-purple-600 text-white'
                    } transition-colors`}
                    title="Swap input and output"
                  >
                    <Shuffle size={16} />
                    <span>Swap</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={generateSampleText}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-blue-600 border-blue-500 hover:bg-blue-700 text-white' : 'bg-blue-500 border-blue-400 hover:bg-blue-600 text-white'
                    } transition-colors`}
                    title="Generate sample text"
                  >
                    <Key size={16} />
                    <span>Sample</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={resetAll}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <RotateCcw size={16} />
                    <span>Reset</span>
                  </button>
                </div>
              </div>
              
              {/* Binary Pattern Preview */}
              {showBinary && binaryRepresentation && (
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                  <h4 className="text-sm font-medium mb-3">Binary Pattern ({baconMethod} method):</h4>
                  <div className="font-mono text-sm break-all">
                    {binaryRepresentation.split('').map((bit, index) => (
                      <span
                        key={index}
                        className={`${
                          bit === 'A' 
                            ? (isDarkMode ? 'text-green-400' : 'text-green-600')
                            : (isDarkMode ? 'text-red-400' : 'text-red-600')
                        } ${index % 5 === 4 ? 'mr-2' : ''}`}
                      >
                        {bit}
                      </span>
                    ))}
                  </div>
                  <div className="text-xs opacity-60 mt-2">
                    {Math.ceil(binaryRepresentation.length / 5)} characters encoded in {binaryRepresentation.length} binary positions
                  </div>
                </div>
              )}
            </div>

            {/* Text Areas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">
                    {operation === 'encrypt' ? 'Secret Message' : 'Steganographic Text'}
                  </h3>
                  <div className="text-sm opacity-60">
                    {inputText.length} characters
                  </div>
                </div>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder={operation === 'encrypt' 
                    ? "Enter secret message to hide..." 
                    : "Enter text with hidden message to decode..."
                  }
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
                <div className="mt-2 flex items-center space-x-4">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="file"
                      accept=".txt"
                      onChange={handleFileUpload}
                      className="sr-only"
                    />
                    <Upload size={16} className="mr-1" />
                    <span className="text-sm">Upload File</span>
                  </label>
                </div>
              </div>

              {/* Output */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">
                    {operation === 'encrypt' ? 'Steganographic Text' : 'Decoded Message'}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm opacity-60">
                      {outputText.length} characters
                    </div>
                    <button
                      onClick={() => copyToClipboard(outputText)}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Copy to clipboard"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      onClick={() => downloadText(outputText, 'bacon-output.txt')}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Download as file"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
                <textarea
                  value={outputText}
                  readOnly
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>
            </div>

            {/* Cover Text Section - Only show for encryption */}
            {operation === 'encrypt' && (
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Cover Text</h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={generateSampleCoverText}
                      className={`px-3 py-1 text-sm rounded ${
                        isDarkMode ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'
                      } text-white transition-colors`}
                      title="Generate sample cover text"
                    >
                      Sample Cover
                    </button>
                    <div className="text-sm opacity-60">
                      {coverText.length} characters
                    </div>
                  </div>
                </div>
                <textarea
                  value={coverText}
                  onChange={(e) => setCoverText(e.target.value)}
                  placeholder="Enter cover text that will hide your secret message..."
                  className={`w-full h-32 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
                <div className="text-xs opacity-60 mt-2">
                  Cover text should have enough letters to hide your message. Need at least {inputText.replace(/[^A-Za-z]/g, '').length * 5} letters.
                </div>
              </div>
            )}
          </div>
        </main>

        {/* Binary Modal */}
        {activeModal === 'binary' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Binary className="text-purple-500" />
                  <span>Binary Pattern Analysis</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-6">
                {binaryRepresentation && (
                  <>
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Binary Representation</h3>
                      <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} font-mono text-sm`}>
                        {binaryRepresentation.split('').map((bit, index) => (
                          <span
                            key={index}
                            className={`${
                              bit === 'A' 
                                ? (isDarkMode ? 'text-green-400 bg-green-900' : 'text-green-800 bg-green-100')
                                : (isDarkMode ? 'text-red-400 bg-red-900' : 'text-red-800 bg-red-100')
                            } px-1 rounded ${index % 5 === 4 ? 'mr-3' : ''}`}
                          >
                            {bit}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Character Breakdown</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(() => {
                          const alphabet = getBaconAlphabet();
                          const chars = inputText.replace(/[^A-Za-z]/g, '').toUpperCase().split('');
                          return chars.map((char, index) => (
                            <div key={index} className={`p-3 rounded border ${
                              isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                            }`}>
                              <div className="text-center">
                                <div className="text-xl font-bold mb-2">{char}</div>
                                <div className="font-mono text-sm">
                                  {alphabet[char]?.split('').map((bit, bitIndex) => (
                                    <span
                                      key={bitIndex}
                                      className={`${
                                        bit === 'A' 
                                          ? (isDarkMode ? 'text-green-400' : 'text-green-600')
                                          : (isDarkMode ? 'text-red-400' : 'text-red-600')
                                      } px-1`}
                                    >
                                      {bit}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  </>
                )}
                
                <div>
                  <h3 className="text-lg font-semibold mb-3">Bacon Alphabet ({baconMethod})</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 text-xs">
                    {Object.entries(getBaconAlphabet()).map(([letter, code]) => (
                      <div key={letter} className={`p-2 rounded border text-center ${
                        isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="font-bold">{letter}</div>
                        <div className="font-mono">
                          {code.split('').map((bit, index) => (
                            <span
                              key={index}
                              className={bit === 'A' 
                                ? (isDarkMode ? 'text-green-400' : 'text-green-600')
                                : (isDarkMode ? 'text-red-400' : 'text-red-600')
                              }
                            >
                              {bit}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Help Modal */}
        {activeModal === 'help' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Shield className="text-purple-500" />
                  <span>About Bacon Cipher</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="font-semibold mb-2">What is the Bacon Cipher?</h3>
                  <p>The Bacon Cipher, attributed to Francis Bacon (1561-1626), is a steganographic cipher that uses binary encoding to hide messages within other text. It was one of the first systematic binary encoding methods and represents each letter with a unique 5-bit pattern using only two symbols (traditionally A and B).</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">How it Works</h3>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Each letter is encoded as a 5-bit binary pattern using A and B</li>
                    <li>The secret message is converted to this binary representation</li>
                    <li>A cover text is chosen that has enough letters for the binary pattern</li>
                    <li>The binary pattern is hidden using steganographic methods</li>
                    <li>The result appears as normal text but contains the hidden message</li>
                  </ol>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Standard vs Variant</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="text-xs space-y-2">
                      <div><strong>Standard (24 letters):</strong> I and J share ABAAA, U and V share BAABB</div>
                      <div><strong>Variant (26 letters):</strong> All letters have unique codes</div>
                      <div className="mt-2 font-mono">
                        <div>A=AAAAA B=AAAAB C=AAABA D=AAABB E=AABAA</div>
                        <div>F=AABAB G=AABBA H=AABBB I=ABAAA J=ABAAB</div>
                        <div>K=ABABA L=ABABB M=ABBAA N=ABBAB O=ABBBA</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Steganographic Methods</h3>
                  <ul className="space-y-1">
                    <li><strong>Case Method:</strong> Lowercase = A, Uppercase = B</li>
                    <li><strong>Font Method:</strong> Normal text = A, *Italic text* = B</li>
                    <li><strong>Spacing Method:</strong> Single space = A, Double space = B</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Example Encoding</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="font-mono text-xs space-y-1">
                      <div><strong>Secret Message:</strong> "HELLO"</div>
                      <div><strong>Binary:</strong> H=AABBB E=AABAA L=ABABA L=ABABA O=ABBBA</div>
                      <div><strong>Combined:</strong> AABBBAABAAALABAAABABAABBBA</div>
                      <div><strong>Cover Text:</strong> "This is a sample text message"</div>
                      <div><strong>Using Case:</strong> "tHis Is A sAmple TexT mEsSaGe"</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Historical Context</h3>
                  <ul className="space-y-1">
                    <li><strong>Renaissance Era:</strong> Developed during the English Renaissance</li>
                    <li><strong>Scientific Method:</strong> Part of Bacon's systematic approach to knowledge</li>
                    <li><strong>Literary Speculation:</strong> Some claim Shakespeare's works contain Bacon ciphers</li>
                    <li><strong>Binary Pioneer:</strong> Predates modern binary systems by centuries</li>
                    <li><strong>Steganography:</strong> Early example of hiding information in plain sight</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Advantages</h3>
                  <ul className="space-y-1">
                    <li>Completely hidden - appears as normal text</li>
                    <li>No visible cipher text to arouse suspicion</li>
                    <li>Multiple steganographic methods available</li>
                    <li>Can be combined with other encryption methods</li>
                    <li>Simple binary system easy to understand</li>
                    <li>Self-contained - no external key needed</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Weaknesses</h3>
                  <ul className="space-y-1">
                    <li>Requires large cover text for small messages</li>
                    <li>Pattern analysis can reveal steganographic method</li>
                    <li>Statistical analysis of letter frequencies</li>
                    <li>Limited to alphabetic characters only</li>
                    <li>Vulnerable once method is known</li>
                    <li>Difficult to create natural-looking cover text</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Francis Bacon (1561-1626)</h3>
                  <ul className="space-y-1">
                    <li><strong>Philosopher:</strong> Pioneer of the scientific method</li>
                    <li><strong>Statesman:</strong> English Attorney General and Lord Chancellor</li>
                    <li><strong>Writer:</strong> Essays, scientific works, and philosophical treatises</li>
                    <li><strong>Innovator:</strong> Advanced empirical methods and systematic observation</li>
                    <li><strong>Cryptographer:</strong> Developed various cipher and steganographic methods</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Modern Applications</h3>
                  <ul className="space-y-1">
                    <li>Digital steganography and watermarking</li>
                    <li>Educational demonstrations of binary encoding</li>
                    <li>Historical cryptography research and analysis</li>
                    <li>Literary analysis and authorship studies</li>
                    <li>Understanding foundations of binary systems</li>
                    <li>Security awareness and steganography detection</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Detection Methods</h3>
                  <ul className="space-y-1">
                    <li><strong>Statistical Analysis:</strong> Unusual case/formatting patterns</li>
                    <li><strong>Frequency Analysis:</strong> Abnormal character distributions</li>
                    <li><strong>Pattern Recognition:</strong> Regular spacing or formatting</li>
                    <li><strong>Linguistic Analysis:</strong> Unnatural text flow or structure</li>
                    <li><strong>Binary Analysis:</strong> Testing for 5-bit groupings</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Security Level</h3>
                  <p className="text-orange-600">⚠️ <strong>Historical Security:</strong> The Bacon Cipher provided excellent concealment for its time, as the message was completely hidden within normal text. However, modern statistical analysis and computer methods can easily detect and decode Bacon ciphers. It's excellent for education and understanding steganographic principles but not suitable for actual security needs.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {activeModal === 'settings' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Advanced Options</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Show Binary Pattern</label>
                  <input
                    type="checkbox"
                    checked={showBinary}
                    onChange={(e) => setShowBinary(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Group Output</label>
                  <input
                    type="checkbox"
                    checked={groupOutput}
                    onChange={(e) => setGroupOutput(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                {groupOutput && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Group Size</label>
                    <input
                      type="range"
                      min="3"
                      max="10"
                      value={groupSize}
                      onChange={(e) => setGroupSize(parseInt(e.target.value))}
                      className="w-full"
                    />
                    <div className="text-xs opacity-60 mt-1">
                      Characters per group: {groupSize}
                    </div>
                  </div>
                )}
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Steganographic Method</h3>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="steganographicMethod"
                        value="case"
                        checked={steganographicMethod === 'case'}
                        onChange={(e) => setSteganographicMethod(e.target.value as 'case')}
                        className="mr-2"
                      />
                      <span className="text-sm">Case (lowercase=A, UPPERCASE=B)</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="steganographicMethod"
                        value="font"
                        checked={steganographicMethod === 'font'}
                        onChange={(e) => setSteganographicMethod(e.target.value as 'font')}
                        className="mr-2"
                      />
                      <span className="text-sm">Font (normal=A, *italic*=B)</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="steganographicMethod"
                        value="spacing"
                        checked={steganographicMethod === 'spacing'}
                        onChange={(e) => setSteganographicMethod(e.target.value as 'spacing')}
                        className="mr-2"
                      />
                      <span className="text-sm">Spacing (single=A, double=B)</span>
                    </label>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Historical Presets</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setBaconMethod('standard');
                        setSteganographicMethod('case');
                        setShowBinary(true);
                        setGroupOutput(false);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-purple-600 border-purple-500 hover:bg-purple-700 text-white' : 'bg-purple-500 border-purple-400 hover:bg-purple-600 text-white'
                      } transition-colors`}
                    >
                      Francis Bacon Original
                    </button>
                    
                    <button
                      onClick={() => {
                        setBaconMethod('variant');
                        setSteganographicMethod('font');
                        setShowBinary(true);
                        setGroupOutput(true);
                        setGroupSize(5);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-blue-600 border-blue-500 hover:bg-blue-700 text-white' : 'bg-blue-500 border-blue-400 hover:bg-blue-600 text-white'
                      } transition-colors`}
                    >
                      Modern Educational
                    </button>
                    
                    <button
                      onClick={() => {
                        setBaconMethod('standard');
                        setSteganographicMethod('spacing');
                        setShowBinary(false);
                        setGroupOutput(false);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                      } transition-colors`}
                    >
                      Stealth Mode
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Modal */}
        {activeModal === 'analytics' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Analytics Dashboard</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-green-500">{stats.totalEncryptions}</div>
                  <div className="text-sm">Messages Hidden</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-blue-500">{stats.totalDecryptions}</div>
                  <div className="text-sm">Messages Extracted</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-purple-500">{stats.binaryGroupsProcessed}</div>
                  <div className="text-sm">Binary Groups</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-orange-500">{stats.totalCharactersProcessed}</div>
                  <div className="text-sm">Characters Processed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-pink-500">{stats.uniqueCoverTexts.size}</div>
                  <div className="text-sm">Unique Cover Texts</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-cyan-500">{stats.averageCoverTextLength}</div>
                  <div className="text-sm">Avg Cover Length</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-amber-500">{stats.averageBinaryGroups}</div>
                  <div className="text-sm">Avg Binary Groups</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-red-500">{stats.longestText}</div>
                  <div className="text-sm">Longest Message</div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Recent Operations</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {history.length > 0 ? history.slice(0, 15).map(item => (
                    <div key={item.id} className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            item.operation === 'encrypt' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                          }`}>
                            {item.operation === 'encrypt' ? 'HIDE' : 'EXTRACT'}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-purple-500 text-white">
                            {item.binaryGroups} groups
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-orange-500 text-white">
                            {item.method}
                          </span>
                        </div>
                        <span className="text-xs opacity-60">
                          {item.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-sm">
                        <div className="truncate font-mono">{item.input}</div>
                        <div className="truncate opacity-60 font-mono">→ {item.output}</div>
                        {item.coverText && (
                          <div className="truncate opacity-40 text-xs mt-1">Cover: {item.coverText}</div>
                        )}
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 opacity-60">
                      <Binary size={32} className="mx-auto mb-2" />
                      <div>No operations performed yet</div>
                      <div className="text-xs">Start hiding or extracting messages to see analytics</div>
                    </div>
                  )}
                </div>
              </div>
              
              {history.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="text-sm font-medium mb-2">Steganographic Statistics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div>
                      <div className="text-purple-500">Most Used Method:</div>
                      <div>
                        {history.length > 0 
                          ? (() => {
                              const methods = history.reduce((acc, item) => {
                                const key = `${item.method}`;
                                acc[key] = (acc[key] || 0) + 1;
                                return acc;
                              }, {} as Record<string, number>);
                              return Object.keys(methods).reduce((a, b) => methods[a] > methods[b] ? a : b);
                            })()
                          : 'N/A'
                        }
                      </div>
                    </div>
                    <div>
                      <div className="text-green-500">Hide/Extract Ratio:</div>
                      <div>{stats.totalOperations > 0 ? Math.round((stats.totalEncryptions / stats.totalOperations) * 100) : 0}% Hide</div>
                    </div>
                    <div>
                      <div className="text-blue-500">Most Active Time:</div>
                      <div>{history.length > 0 ? new Date(Math.max(...history.map(h => h.timestamp.getTime()))).toLocaleTimeString() : 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-orange-500">Steganographic Efficiency:</div>
                      <div>{stats.totalCharactersProcessed > 0 ? Math.round((stats.binaryGroupsProcessed * 5 / stats.totalCharactersProcessed) * 100) : 0}% Binary</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BaconCipherApp;